import type { StaticEntityType } from "@config";
import { produce } from "immer";

type RichTextSettings = {
  name?: string;
  content?: string;
};

type TreeNode = { settings?: unknown; children?: unknown[] };

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isRichTextSettings = (value: unknown): value is RichTextSettings =>
  isObject(value) && (value as { name?: unknown }).name === "RichText";

const childrenOf = (node: TreeNode): TreeNode[] =>
  Array.isArray(node.children) ? (node.children as TreeNode[]) : [];

// Empty slots are pushed as "" so positions stay aligned with injectRichTextOrdered.
export const extractRichTextOrdered = (
  entity: TreeNode | null | undefined,
): string[] => {
  const collected: string[] = [];
  const visit = (node: TreeNode | null) => {
    if (!node || !isObject(node)) return;
    if (isRichTextSettings(node.settings)) {
      const content = node.settings.content;
      collected.push(typeof content === "string" ? content : "");
    }
    for (const child of childrenOf(node)) visit(child);
  };
  visit(entity ?? null);
  return collected;
};

const countRichTextNodes = (entity: TreeNode | null | undefined): number => {
  let count = 0;
  const visit = (node: TreeNode | null) => {
    if (!node || !isObject(node)) return;
    if (isRichTextSettings(node.settings)) count++;
    for (const child of childrenOf(node)) visit(child);
  };
  visit(entity ?? null);
  return count;
};

// When swapping a slide's layout we sometimes carry the *current* slide's
// typed rich text into the incoming slide, matched positionally. That is the
// intended behaviour for layout-apply and AI-regenerate (keep what the user
// typed, change the design).
//
// It must NOT happen for content-bearing inserts — saved slides, templates —
// which arrive with their own real content. Injecting the current slide's
// (usually empty) placeholder text over them wipes the content. This is the
// bug behind "Saved slides text disappears via Blank slide > More Layouts >
// Saved slides": the just-created blank slide leaves an empty title + an
// empty-paragraph HTML body, and because that body is a non-empty *string*
// the `injectRichTextOrdered` empty-check does not protect it.
//
// `preserve` makes the intent explicit at the call site instead of relying on
// that fragile guard.
export const carryOverRichText = (
  incoming: StaticEntityType,
  current: TreeNode | null | undefined,
  preserve: boolean,
): StaticEntityType =>
  preserve
    ? injectRichTextOrdered(incoming, extractRichTextOrdered(current))
    : incoming;

export const injectRichTextOrdered = (
  entity: StaticEntityType,
  contents: string[],
): StaticEntityType => {
  if (contents.every((c) => !c)) return entity;
  // Abort if structure has drifted — index-based mapping would corrupt content.
  if (countRichTextNodes(entity as unknown as TreeNode) !== contents.length) {
    return entity;
  }
  return produce(entity, (draft) => {
    let idx = 0;
    const visit = (node: TreeNode | null) => {
      if (!node || !isObject(node)) return;
      if (isRichTextSettings(node.settings)) {
        const captured = contents[idx];
        if (captured !== undefined) node.settings.content = captured;
        idx++;
      }
      for (const child of childrenOf(node)) visit(child);
    };
    visit(draft as unknown as TreeNode);
  });
};
